import { useState, useEffect, useRef, useCallback, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { useLang, LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Loader2, Mail, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global fetch monkey-patch to send Authorization header for Safari / iPad cookie compatibility
const originalFetch = window.fetch;
// El token se lee de localStorage una vez y se memoriza; antes se hacia
// getItem + JSON.parse en CADA fetch (la PDA dispara decenas por minuto).
// Se refresca cuando cambia la llave (login/logout escriben mos_user).
let _cachedRaw = null;
let _cachedToken = "";
const sessionToken = () => {
  try {
    const stored = localStorage.getItem("mos_user");
    if (stored !== _cachedRaw) {
      _cachedRaw = stored;
      _cachedToken = (stored && JSON.parse(stored)?.session_token) || "";
    }
  } catch (e) {
    console.error("Fetch interceptor error:", e);
  }
  return _cachedToken;
};
window.fetch = async function (url, options = {}) {
  const token = sessionToken();
  if (token) {
    options.headers = { ...options.headers, "Authorization": `Bearer ${token}` };
  }
  return originalFetch.call(this, url, options);
};

// Roles that are isolated to the QC dashboard only (like operators are locked
// to /operator). They land on /qc at login and get bounced back if they try to
// reach any other route. Admin/supersu also use QC but are NOT confined.
const QC_ROLES = ['qc', 'inspector_qc'];

// Vista ejecutiva: SOLO rol ceo y supersu (por unión de roles; el backend
// exige lo mismo en /api/executive/*). Los admin ya no la ven.
const EXEC_ROLES = ['ceo', 'supersu'];
const isExecUser = (u) => [...(u?.roles || []), u?.role].some(r => EXEC_ROLES.includes(r));

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("mos_user");
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(!user);

  const checkAuth = useCallback(async (isPeriodicCheck = false) => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Check if we have session_token in URL hash (workaround for Safari cookie policy on iPad)
    const hash = window.location.hash;
    const sessionTokenMatch = hash.match(/session_token=([^&]+)/);
    let token = null;
    if (sessionTokenMatch) {
      token = sessionTokenMatch[1];
      // Clean the hash so it doesn't clutter the URL
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    
    try {
      const stored = localStorage.getItem("mos_user");
      let storedUser = null;
      try { storedUser = stored ? JSON.parse(stored) : null; } catch {}
      const bearerToken = token || storedUser?.session_token;
      
      const fetchOptions = { credentials: 'include' };
      if (bearerToken) {
        fetchOptions.headers = {
          "Authorization": `Bearer ${bearerToken}`
        };
      }

      const response = await fetch(`${API}/auth/me`, fetchOptions);
      if (response.ok) {
        const userData = await response.json();
        if (bearerToken) {
          userData.session_token = bearerToken;
        }
        setUser(userData);
      } else if (response.status === 401) {
        // Session expired or invalid — always clear and redirect to stop infinite loop
        console.warn("Session invalid (401), clearing state and redirecting to login...");
        setUser(null);
        localStorage.removeItem("mos_user");
        if (!isPeriodicCheck) {
          // Only redirect if this is the initial check (not heartbeat) and not already on home
          if (window.location.pathname !== '/') {
            window.location.replace('/');
          }
        }
      }
    } catch (error) {
      // Network error — don't loop, just fail gracefully
      console.error("Auth check failed (network):", error);
    } finally {
      if (!isPeriodicCheck) setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Periodic session heartbeat: verify session every 10 minutes
  useEffect(() => {
    const heartbeat = setInterval(() => {
      checkAuth(true);
    }, 10 * 60 * 1000); // 10 minutes
    return () => clearInterval(heartbeat);
  }, [checkAuth]);

  // Persistent user sync
  useEffect(() => {
    if (user) {
      localStorage.setItem("mos_user", JSON.stringify(user));
    } else {
      localStorage.removeItem("mos_user");
    }
  }, [user]);

  // Global tab-close guard
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Recargas decididas por la app (nuevo deploy, SW nuevo, ErrorBoundary)
      // no deben pedir confirmacion: ver programmaticReload() en index.js.
      if (window.__mosProgrammaticReload) return;
      if (user) {
        e.preventDefault();
        e.returnValue = ""; // Standard browser requirement
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [user]);

  const login = () => {
    window.location.href = `${BACKEND_URL}/api/auth/google`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem("mos_user");
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const { t } = useLang();
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        // Retry up to 3 times for network issues
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const response = await fetch(`${API}/auth/session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ session_id: sessionId })
            });

            if (response.ok) {
              const userData = await response.json();
              // Set user in context FIRST, then wait for state to propagate
              setUser(userData);
              // Small delay to ensure React state propagates before navigation
              await new Promise(r => setTimeout(r, 100));
              if (QC_ROLES.includes(userData.role)) {
                navigate('/qc', { replace: true });
              } else if (userData.role === 'customer') {
                // Cliente: directo a su dashboard de inventario (en /wms). Sin
                // esto caia en /dashboard y las guardas de ruta lo rebotaban,
                // mostrando varios dashboards antes del suyo.
                navigate('/wms', { replace: true });
              } else if (isExecUser(userData)) {
                navigate('/ejecutivo', { replace: true });
              } else if (userData.role === 'general') {
                navigate('/dashboard', { replace: true });
              } else {
                navigate('/dashboard', { replace: true });
              }
              return;
            } else {
              const errData = await response.json().catch(() => ({}));
              console.error(`Auth session failed (attempt ${attempt}):`, errData);
              if (attempt === 3) setError(errData.detail || 'Error de autenticación');
            }
          } catch (err) {
            console.error(`Auth callback network error (attempt ${attempt}):`, err);
            if (attempt < 3) await new Promise(r => setTimeout(r, 1000));
            else setError('Error de conexión. Intenta de nuevo.');
          }
        }
      }
      // Only redirect to home if no session_id was found
      if (!sessionIdMatch) navigate('/', { replace: true });
    };

    processAuth();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-destructive font-barlow text-xl">{error}</div>
          <button onClick={() => window.location.href = '/'} 
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-semibold text-sm">
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-foreground font-barlow text-xl">{t('processing_auth')}</div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowCustomer = false, supersuOnly = false }) => {
  const { t } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [grace, setGrace] = useState(true);

  // Grace period: wait 1.5s before redirecting to allow setUser to propagate from AuthCallback
  useEffect(() => {
    const timer = setTimeout(() => setGrace(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!loading && !grace) {
      if (!user) {
        navigate('/', { replace: true });
      } else if (supersuOnly && user.role !== 'supersu') {
        // Conciliación física: reservada al super usuario. Aquí solo se oculta;
        // el backend es quien de verdad rechaza (403).
        navigate('/wms', { replace: true });
      } else if (user.role === 'customer' && !allowCustomer) {
        navigate('/wms', { replace: true });
      } else if (user.role === 'picker' && !['/wms', '/pda', '/pda-recon', '/pda-foto'].includes(window.location.pathname)) {
        // Pickers use the WMS launcher: "Picking" returns to their original /pda
        // view, "Putaway 2.0" is the WMS module. Allow those; bounce anything
        // else back to the launcher (covers the iPad PWA entry too).
        // "/pda-recon" (Conciliación) SÍ está permitida para pickers.
        navigate('/wms', { replace: true });
      } else if (QC_ROLES.includes(user.role) && window.location.pathname !== '/qc') {
        // QC inspectors are locked to the QC dashboard, like operators to /operator.
        navigate('/qc', { replace: true });
      }
    }
  }, [user, loading, grace, navigate, allowCustomer]);

  if (loading || (grace && !user)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-barlow text-xl">{t('loading')}</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { t } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [grace, setGrace] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setGrace(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!loading && !grace) {
      if (!user) {
        navigate('/', { replace: true });
      } else if (QC_ROLES.includes(user.role)) {
        // QC inspectors are isolated to /qc — no access to admin pages.
        navigate('/qc', { replace: true });
      } else if (!['admin', 'supersu'].includes(user.role)) {
        if (user.role === 'customer') {
          navigate('/wms', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    }
  }, [user, loading, grace, navigate]);

  if (loading || (grace && !user)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-barlow text-xl">{t('loading')}</div>
      </div>
    );
  }

  if (!user || !['admin', 'supersu'].includes(user.role)) {
    return null;
  }

  return children;
};

const ExecRoute = ({ children }) => {
  const { t } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [grace, setGrace] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setGrace(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!loading && !grace) {
      if (!user) {
        navigate('/', { replace: true });
      } else if (!isExecUser(user)) {
        navigate(QC_ROLES.includes(user.role) ? '/qc' : '/dashboard', { replace: true });
      }
    }
  }, [user, loading, grace, navigate]);

  if (loading || (grace && !user)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-barlow text-xl">{t('loading')}</div>
      </div>
    );
  }

  if (!user || !isExecUser(user)) {
    return null;
  }

  return children;
};

// Landing Page
const LandingPage = () => {
  const { t } = useLang();
  const { login, user, setUser } = useAuth();
  const navigate = useNavigate();
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    // Only redirect if user is confirmed authenticated (has a real user_id)
    if (user && user.user_id) {
      if (user.role === 'picker') {
        navigate('/wms', { replace: true });
      } else if (user.role === 'customer') {
        navigate('/wms', { replace: true });
      } else if (user.role === 'operator') {
        navigate('/operator', { replace: true });
      } else if (QC_ROLES.includes(user.role)) {
        navigate('/qc', { replace: true });
      } else if (['admin', 'supersu', 'ceo'].includes(user.role)) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoginLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ email, password })
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        if (QC_ROLES.includes(userData.role)) {
          navigate('/qc', { replace: true });
        } else if (userData.role === 'customer') {
          navigate('/wms', { replace: true });
        } else if (isExecUser(userData)) {
          navigate('/ejecutivo', { replace: true });
        } else if (userData.role === 'picker') {
          navigate('/wms', { replace: true });
        } else if (userData.role === 'operator') {
          navigate('/operator', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Error al iniciar sesion');
      }
    } catch { toast.error('Error de conexion'); }
    finally { setLoginLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      setForgotSent(true);
      toast.success(data.message || 'Email enviado');
      if (data.reset_link) {
        console.log('Reset link (dev):', data.reset_link);
      }
    } catch { toast.error('Error de conexion'); }
    finally { setForgotLoading(false); }
  };

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden bg-background">
      <Toaster position="bottom-right" theme="dark" />

      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img src="/tech_bg.jpg" alt="Tech Background" className="w-full h-full object-cover opacity-80 mix-blend-screen" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-12 flex flex-col flex-1">
        {/* Header */}
        <header className="flex justify-between items-center mb-10 md:mb-16">
          <div className="flex items-center gap-4 md:gap-6">
            <img
              src="/prosper_logo.jpg"
              alt="Prosper Manufacturing"
              className="h-16 md:h-24 w-auto object-cover rounded-2xl shadow-xl drop-shadow-[0_5px_15px_rgba(0,0,0,0.6)] hover:scale-105 transition-transform"
            />
          </div>
        </header>

        {/* Hero & Form Container */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center justify-between flex-1">
          {/* Text Content */}
          <div className="flex-1 text-center lg:text-left z-10 w-full">
            <h2 className="font-barlow font-bold text-4xl md:text-5xl lg:text-7xl text-foreground leading-tight mb-4 md:mb-6 uppercase tracking-wide drop-shadow-md">
              {t('landing_hero_1')}<br />
              <span className="text-primary">{t('landing_hero_2')}</span>
            </h2>
            <p className="text-muted-foreground text-base md:text-lg lg:text-xl mb-6 max-w-2xl mx-auto lg:mx-0 font-medium">
              {t('landing_desc')}
            </p>

            {/* Dynamic System Badge */}
            <div className="mt-8 mb-10 md:mb-12 inline-block">
              <div className="flex items-center gap-4 px-6 py-4 bg-card/80 backdrop-blur-xl rounded-2xl border border-primary/30 shadow-[0_0_40px_rgba(255,193,7,0.15)] relative overflow-hidden group hover:border-primary/60 transition-colors cursor-default">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                {/* Sin animate-pulse: era una animación infinita del compositor
                    en una pantalla que se queda abierta; el glow estático da la
                    misma señal de "sistema vivo". */}
                <div className="w-1.5 h-8 bg-primary rounded-full shadow-[0_0_15px_rgba(255,193,7,0.9)]"></div>
                <div className="flex flex-col text-left">
                  <span className="text-[11px] uppercase tracking-[0.25em] text-primary font-bold">Arquitectura Inteligente</span>
                  <span className="text-xl md:text-2xl font-black tracking-widest text-foreground font-barlow drop-shadow-md">
                    CRM INDUSTRIAL
                  </span>
                </div>
              </div>
            </div>

            <div className="hidden xl:grid grid-cols-3 gap-6 mt-4">
              {[
                { title: t('landing_feature_1_title'), desc: t('landing_feature_1_desc') },
                { title: t('landing_feature_2_title'), desc: t('landing_feature_2_desc') },
                { title: t('landing_feature_3_title'), desc: t('landing_feature_3_desc') }
              ].map((feature, i) => (
                <div key={feature.title} className="bg-card/40 backdrop-blur-md border border-white/5 rounded-xl p-6 hover:bg-card/60 hover:border-primary/30 transition-all group">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    {i === 0 ? <div className="w-4 h-4 bg-primary rounded-sm shadow-[0_0_15px_rgba(220,38,38,0.8)]" /> :
                     i === 1 ? <div className="w-4 h-4 border-2 border-primary rounded-full shadow-[0_0_15px_rgba(220,38,38,0.8)]" /> :
                     <div className="w-4 h-1 bg-primary rounded shadow-[0_0_15px_rgba(220,38,38,0.8)]" />}
                  </div>
                  <h3 className="font-barlow font-bold text-lg text-foreground mb-2 uppercase tracking-wider">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Login Card (Glassmorphism) */}
          <div className="w-full max-w-md z-10">
            <div className="bg-black/60 backdrop-blur-3xl border border-primary/40 rounded-3xl p-8 md:p-10 shadow-[0_0_50px_rgba(255,193,7,0.2)] relative overflow-hidden group">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-80"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/30 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2"></div>
              
              <h3 className="text-2xl font-bold font-barlow text-foreground mb-8 text-center drop-shadow-sm">Acceso al Sistema</h3>

              {showForgot ? (
                <div className="space-y-4 relative z-10">
                  <button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors" data-testid="back-to-login">
                    <ArrowLeft className="w-4 h-4" /> Volver
                  </button>
                  <h3 className="font-barlow font-bold text-xl text-foreground">Recuperar contraseña</h3>
                  {forgotSent ? (
                    <div className="bg-primary/20 border border-primary/40 rounded-lg p-4 text-sm text-foreground backdrop-blur-sm" data-testid="forgot-success-msg">
                      Si el email existe en el sistema, recibirás un enlace para restablecer tu contraseña.
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder="tu@email.com" required
                          className="w-full pl-10 pr-4 py-3.5 bg-black/50 backdrop-blur-lg border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500/80 focus:border-red-500/50 transition-all placeholder:text-white/40 font-medium selection:bg-red-500/30"
                          data-testid="forgot-email-input" />
                      </div>
                      <button type="submit" disabled={forgotLoading}
                        className="w-full py-4 bg-gradient-to-r from-red-600 to-zinc-700 hover:from-red-500 hover:to-zinc-600 text-white rounded-xl font-black tracking-widest text-sm transition-all hover:-translate-y-0.5 shadow-[0_0_20px_rgba(220,38,38,0.4)] hover:shadow-[0_0_30px_rgba(220,38,38,0.6)] border border-red-500/50 flex items-center justify-center gap-2 disabled:opacity-50 mt-4"
                        data-testid="forgot-submit-btn">
                        {forgotLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Enviar enlace de recuperación
                      </button>
                    </form>
                  )}
                </div>
              ) : showEmailLogin ? (
                <div className="space-y-5 relative z-10">
                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="Correo electrónico" required autoComplete="email"
                        className="w-full pl-10 pr-4 py-3.5 bg-black/50 backdrop-blur-lg border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500/80 focus:border-red-500/50 transition-all placeholder:text-white/40 font-medium selection:bg-red-500/30"
                        data-testid="login-email-input" />
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input type={showPass ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="Contraseña" required autoComplete="current-password"
                        className="w-full pl-10 pr-10 py-3.5 bg-black/50 backdrop-blur-lg border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500/80 focus:border-red-500/50 transition-all placeholder:text-white/40 font-medium selection:bg-red-500/30"
                        data-testid="login-password-input" />
                      <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    
                    <div className="flex justify-end">
                       <button type="button" onClick={() => setShowForgot(true)} className="text-sm font-medium text-primary hover:text-primary/80 hover:underline transition-colors" data-testid="forgot-password-link">
                         ¿Olvidaste tu contraseña?
                       </button>
                    </div>

                    <button type="submit" disabled={loginLoading}
                      className="w-full py-4 bg-gradient-to-r from-red-600 to-zinc-700 hover:from-red-500 hover:to-zinc-600 text-white rounded-xl font-black tracking-widest text-sm transition-all hover:-translate-y-0.5 shadow-[0_0_20px_rgba(220,38,38,0.4)] hover:shadow-[0_0_30px_rgba(220,38,38,0.6)] border border-red-500/50 flex items-center justify-center gap-2 disabled:opacity-50 mt-4 relative overflow-hidden"
                      data-testid="login-email-submit-btn">
                      <div className="absolute inset-0 bg-white/20 translate-y-full hover:translate-y-0 transition-transform duration-300"></div>
                      <span className="relative flex items-center gap-2">
                        {loginLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                        {loginLoading ? t('processing_auth') : 'INICIAR SESIÓN'}
                      </span>
                    </button>
                  </form>
                  
                  <div className="flex items-center gap-3 text-muted-foreground text-xs py-2">
                    <div className="flex-1 border-t border-white/10"></div>
                    <span className="uppercase tracking-widest font-semibold text-muted-foreground/70">O INVENTARIO CON</span>
                    <div className="flex-1 border-t border-white/10"></div>
                  </div>
                  
                  <button onClick={login} data-testid="login-google-btn-alt"
                    className="w-full py-3.5 bg-secondary/60 backdrop-blur-md border border-white/10 rounded-xl text-foreground text-sm font-semibold hover:bg-secondary/80 transition-all flex items-center justify-center gap-3 hover:border-white/20">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google
                  </button>
                  <button onClick={() => setShowEmailLogin(false)} className="mx-auto flex items-center text-xs text-muted-foreground hover:text-foreground transition-colors mt-4" data-testid="back-to-landing">
                    <ArrowLeft className="w-3 h-3 inline mr-1" /> Volver a opciones
                  </button>
                </div>
              ) : (
                <div className="space-y-4 relative z-10 w-full flex flex-col items-center">
                  <button data-testid="login-google-btn" onClick={login}
                    className="w-full bg-gradient-to-r from-white to-gray-100 hover:from-red-600 hover:to-zinc-700 hover:text-white text-black px-8 py-4 rounded-xl font-black tracking-widest text-lg transition-all hover:-translate-y-1 flex items-center justify-center gap-3 border border-white/40 shadow-[0_0_20px_rgba(255,255,255,0.15)] hover:shadow-[0_0_40px_rgba(220,38,38,0.4)] group">
                    <svg className="w-7 h-7 bg-white rounded-full p-1" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t('landing_login')}
                  </button>
                  <button onClick={() => setShowEmailLogin(true)} data-testid="show-email-login-btn"
                    className="w-full py-4 bg-secondary/50 backdrop-blur-md border border-white/10 rounded-xl font-semibold tracking-wide text-sm text-foreground hover:bg-secondary/80 hover:border-white/20 transition-all flex items-center justify-center gap-2 group">
                    <Mail className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                    Ingresar con Email
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// HomeDashboard también va en lazy: arrastraba GlobalColumnManager,
// FormulaEditor, NewOrderForm y ui/select al bundle inicial, y la PDA de
// surtido (/pda) y el WMS los descargaban sin usarlos. El "spinner flash" al
// entrar a la oficina lo cubre el mismo Suspense que ya envuelve las rutas.
const HomeDashboard = lazy(() => import("./components/HomeDashboard"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const WMS = lazy(() => import("./components/WMS"));
const OperatorView = lazy(() => import("./components/OperatorView"));
const PdaPicker = lazy(() => import("./components/pda/PdaPicker"));
const PdaRecon = lazy(() => import("./components/pda/PdaRecon"));
const PdaPhoto = lazy(() => import("./components/pda/PdaPhoto"));
const AutomationCenter = lazy(() => import("./components/AutomationCenter"));
const ActivityLogCenter = lazy(() => import("./components/ActivityLogCenter"));
const LogsCenter = lazy(() => import("./components/LogsCenter"));
const UserManagementCenter = lazy(() => import("./components/UserManagementCenter"));
const CatalogCenter = lazy(() => import("./components/CatalogCenter"));
const QCSettingsPage = lazy(() => import("./components/QCSettingsPage"));
const OperatorsCenter = lazy(() => import("./components/OperatorsCenter"));
const ExecutiveHub = lazy(() => import("./components/executive/ExecutiveHub"));
const QCDashboard = lazy(() => import("./components/QCDashboard"));
const BackupCenter = lazy(() => import("./components/BackupCenter"));
const ArtModule = lazy(() => import("./components/ArtModule"));
const FinalBillModule = lazy(() => import("./components/FinalBillModule"));
const OrderComponentsBoard = lazy(() => import("./components/OrderComponentsBoard"));
const PlannerModule = lazy(() => import("./components/PlannerModule"));
const MachineStatusModule = lazy(() => import("./components/MachineStatusModule"));
const ProduccionModule = lazy(() => import("./components/ProduccionModule"));
const PaintModule = lazy(() => import("./components/PaintModule"));
const PaintStation = lazy(() => import("./components/PaintStation"));
const SamplesModule = lazy(() => import("./components/SamplesModule"));
const CuadrosModule = lazy(() => import("./components/CuadrosModule"));
const BlanksModule = lazy(() => import("./components/BlanksModule"));
const NeckModule = lazy(() => import("./components/NeckModule"));
const EmpaqueModule = lazy(() => import("./components/EmpaqueModule"));
const PackStation = lazy(() => import("./components/PackStation"));
const SmartAgenda = lazy(() => import("./components/SmartAgenda"));
const ShippingModule = lazy(() => import("./components/ShippingModule"));
const GuestShipping = lazy(() => import("./components/GuestShipping"));
const PackingListTool = lazy(() => import("./components/PackingList"));
const ScheduledReports = lazy(() => import("./components/ScheduledReports"));
const PrintavoSync = lazy(() => import("./components/PrintavoSync"));
const PrintavoExport = lazy(() => import("./components/PrintavoExport"));
const PlantillasPO = lazy(() => import("./components/PlantillasPO"));
const BlockerTool = lazy(() => import("./components/BlockerTool"));
const SheetsPage = lazy(() => import("./sheets/SheetsPage"));

// Reset Password Page
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const token = new URLSearchParams(window.location.search).get('token');

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirmPass) { toast.error('Las contrasenas no coinciden'); return; }
    if (password.length < 6) { toast.error('Minimo 6 caracteres'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      if (res.ok) { setDone(true); toast.success('Contrasena actualizada'); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Error'); }
    } catch { toast.error('Error de conexion'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Toaster position="bottom-right" theme="dark" />
      <div className="w-full max-w-sm space-y-6">
        <h1 className="font-barlow font-bold text-3xl text-foreground text-center">
          CRM<span className="text-primary">PROD</span>
        </h1>
        {done ? (
          <div className="text-center space-y-4" data-testid="reset-success">
            <p className="text-foreground">Contrasena actualizada exitosamente.</p>
            <button onClick={() => navigate('/')} className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90" data-testid="go-to-login-btn">
              Ir a iniciar sesion
            </button>
          </div>
        ) : !token ? (
          <div className="text-center text-destructive" data-testid="reset-no-token">Token invalido o faltante.</div>
        ) : (
          <form onSubmit={handleReset} className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground text-center">Nueva contrasena</h2>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input type={showPass ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Nueva contrasena (min. 6)" required
                className="w-full pl-10 pr-10 py-3 bg-secondary border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="reset-password-input" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input type={showPass ? 'text' : 'password'} value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)}
                placeholder="Confirmar contrasena" required
                className="w-full pl-10 pr-4 py-3 bg-secondary border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="reset-confirm-input" />
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="reset-submit-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Restablecer contrasena
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  const { user, logout } = useAuth();

  // Check URL fragment for session_id - CRITICAL: detect during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Invitado shipping (proveedor externo): sólo su vista, en cualquier ruta.
  // La seguridad real está en el backend (deps.GUEST_SURFACE, default-deny).
  if (user?.role === 'shipping_guest') {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
        <GuestShipping user={user} onLogout={logout} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    }>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/home" element={
        <ProtectedRoute>
          <HomeDashboard />
        </ProtectedRoute>
      } />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/registros" element={
        <ProtectedRoute>
          <LogsCenter />
        </ProtectedRoute>
      } />
      <Route path="/sheets" element={
        <ProtectedRoute>
          <SheetsPage />
        </ProtectedRoute>
      } />
      {/* La vista CEO vieja vive ahora dentro del menú ejecutivo. */}
      <Route path="/ceo-dashboard" element={<Navigate to="/ejecutivo/analitica" replace />} />
      <Route path="/ejecutivo/:section?" element={
        <ExecRoute>
          <ExecutiveHub />
        </ExecRoute>
      } />
      <Route path="/wms" element={
        <ProtectedRoute allowCustomer={true}>
          <WMS />
        </ProtectedRoute>
      } />
      <Route path="/art" element={
        <ProtectedRoute>
          <ArtModule />
        </ProtectedRoute>
      } />
      <Route path="/final-bill" element={
        <ProtectedRoute>
          <FinalBillModule />
        </ProtectedRoute>
      } />
      <Route path="/order-components" element={
        <ProtectedRoute>
          <OrderComponentsBoard />
        </ProtectedRoute>
      } />
      <Route path="/planeacion" element={
        <ProtectedRoute>
          <PlannerModule />
        </ProtectedRoute>
      } />
      <Route path="/neck" element={
        <ProtectedRoute>
          <NeckModule />
        </ProtectedRoute>
      } />
      <Route path="/empaque" element={
        <ProtectedRoute>
          <EmpaqueModule />
        </ProtectedRoute>
      } />
      <Route path="/empaque/estacion/:machine?" element={
        <ProtectedRoute>
          <PackStation />
        </ProtectedRoute>
      } />
      <Route path="/blanks" element={
        <ProtectedRoute>
          <BlanksModule />
        </ProtectedRoute>
      } />
      <Route path="/cuadros" element={
        <ProtectedRoute>
          <CuadrosModule />
        </ProtectedRoute>
      } />
      <Route path="/paint" element={
        <ProtectedRoute>
          <PaintModule />
        </ProtectedRoute>
      } />
      <Route path="/paint/estacion" element={
        <ProtectedRoute>
          <PaintStation />
        </ProtectedRoute>
      } />
      <Route path="/samples" element={
        <ProtectedRoute>
          <SamplesModule />
        </ProtectedRoute>
      } />
      <Route path="/produccion" element={
        <ProtectedRoute>
          <ProduccionModule />
        </ProtectedRoute>
      } />
      <Route path="/maquinas" element={
        <ProtectedRoute>
          <MachineStatusModule />
        </ProtectedRoute>
      } />
      <Route path="/operator" element={
        <ProtectedRoute>
          <OperatorView />
        </ProtectedRoute>
      } />
      <Route path="/pda" element={
        <ProtectedRoute>
          <PdaPicker />
        </ProtectedRoute>
      } />
      <Route path="/pda-recon" element={
        <ProtectedRoute>
          <PdaRecon />
        </ProtectedRoute>
      } />
      <Route path="/pda-foto" element={
        <ProtectedRoute>
          <PdaPhoto />
        </ProtectedRoute>
      } />
      <Route path="/automation-center" element={
        <AdminRoute>
          <AutomationCenter />
        </AdminRoute>
      } />
      <Route path="/activity-log" element={
        <AdminRoute>
          <ActivityLogCenter />
        </AdminRoute>
      } />
      <Route path="/catalog-center" element={
        <AdminRoute>
          <CatalogCenter />
        </AdminRoute>
      } />
      <Route path="/qc-settings" element={
        <AdminRoute>
          <QCSettingsPage />
        </AdminRoute>
      } />
      <Route path="/reportes-programados" element={
        <AdminRoute>
          <ScheduledReports />
        </AdminRoute>
      } />
      <Route path="/printavo-sync" element={
        <AdminRoute>
          <PrintavoSync />
        </AdminRoute>
      } />
      <Route path="/printavo-export" element={
        <AdminRoute>
          <PrintavoExport />
        </AdminRoute>
      } />
      <Route path="/plantillas-po" element={
        <AdminRoute>
          <PlantillasPO />
        </AdminRoute>
      } />
      <Route path="/blocker-tool" element={
        <ProtectedRoute>
          <BlockerTool />
        </ProtectedRoute>
      } />
      <Route path="/operators-center" element={
        <AdminRoute>
          <OperatorsCenter />
        </AdminRoute>
      } />
      <Route path="/users" element={
        <AdminRoute>
          <UserManagementCenter />
        </AdminRoute>
      } />
      <Route path="/qc" element={
        <ProtectedRoute>
          <QCDashboard />
        </ProtectedRoute>
      } />
      <Route path="/backups" element={
        <AdminRoute>
          <BackupCenter />
        </AdminRoute>
      } />
      <Route path="/agenda" element={
        <ProtectedRoute>
          <SmartAgenda />
        </ProtectedRoute>
      } />
      <Route path="/shipping" element={
        <ProtectedRoute>
          <ShippingModule />
        </ProtectedRoute>
      } />
      <Route path="/packing" element={
        <ProtectedRoute>
          <PackingListTool />
        </ProtectedRoute>
      } />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <AppRouter />
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>

  );
}

export default App;
